// client/src/utils/pokerStats/types.ts
//
// Tipos centralizados del módulo de stats de poker.
// Este archivo NO tiene dependencias de runtime — solo type-level.

// ─────────────────────────────────────────────────────────────────────────────
// Positions
// ─────────────────────────────────────────────────────────────────────────────

export type Position = 'BTN' | 'SB' | 'BB' | 'CO' | 'HJ' | 'EP' | 'OTHER';

// ─────────────────────────────────────────────────────────────────────────────
// Game format / stack bucket
// ─────────────────────────────────────────────────────────────────────────────

export type GameFormat = 'MTT' | 'Bounty' | 'AoF' | 'Satellite' | 'SNG' | 'Spin' | 'Other';
export type StackBucket = 'short' | 'mid' | 'deep';

// ─────────────────────────────────────────────────────────────────────────────
// Flop texture
// ─────────────────────────────────────────────────────────────────────────────

export type FlopTexture = 'dry' | 'wet' | 'paired' | 'monotone' | 'semi-wet';

// ─────────────────────────────────────────────────────────────────────────────
// Matchup stats (Fase 1 — CBet Flop por matchup posicional)
// ─────────────────────────────────────────────────────────────────────────────

// Claves como template literal. Incluye 'multi' para 3+ jugadores al flop.
export type MatchupKey =
  | `${Position}_vs_${Position}`
  | 'multi';

export interface MatchupStats {
  opp: number;      // oportunidades (veces que se dio el matchup)
  count: number;    // veces que Hero ejecutó la acción (ej: cbet)
  success: number;  // veces que Hero ganó sin ver siguiente street
}

// Helper para crear una clave de matchup.
export function makeMatchupKey(heroPos: Position, villainPos: Position): MatchupKey {
  return `${heroPos}_vs_${villainPos}` as MatchupKey;
}

// ─────────────────────────────────────────────────────────────────────────────
// Orden de acción preflop (primero a actuar → último a actuar)
// Esto es CRÍTICO para determinar matchups válidos de 3bet.
// Solo podés 3betear a alguien que actuó ANTES que vos.
// ─────────────────────────────────────────────────────────────────────────────
export const PREFLOP_ACTION_ORDER: Position[] = ['EP', 'HJ', 'CO', 'BTN', 'SB', 'BB'];

/**
 * ¿Hero actúa DESPUÉS que opener en preflop?
 *
 * En mesas short-handed (3-5 jugadores) los seats tempranos se colapsan en el
 * label "EP" o "HJ" por la lógica de getPlayerPosition, lo que genera matchups
 * imposibles como "EP_vs_EP" o "HJ_vs_BTN". Usar esta función ANTES de trackear
 * un matchup para filtrar ruido.
 *
 * Retorna true solo si hero puede verdaderamente enfrentar el open de opener
 * (i.e. opener actuó estrictamente antes).
 */
export function heroActsAfterOpener(heroPos: Position, openerPos: Position): boolean {
  if (heroPos === 'OTHER' || openerPos === 'OTHER') return false;
  const heroIdx = PREFLOP_ACTION_ORDER.indexOf(heroPos);
  const openerIdx = PREFLOP_ACTION_ORDER.indexOf(openerPos);
  if (heroIdx === -1 || openerIdx === -1) return false;
  return heroIdx > openerIdx;
}

// ─────────────────────────────────────────────────────────────────────────────
// Fase C — Fold to Push: hero enfrenta all-in shove preflop de villain
// ─────────────────────────────────────────────────────────────────────────────
//
// Detectamos cuándo un villain hizo all-in preflop y hero tuvo la decisión
// de call o fold. Cubrimos open-shoves, 3bet-shoves y squeeze-shoves.
//
// Stack bins del shover (en BB):
//   micro : ≤10bb  → shover tiene rango muy wide, hero debería call frecuente
//   short : 10-20bb → rango más tight, hero puede fold un poco más
//   mid   : 20-30bb → rango aún más tight, hero fold bastante más
//   deep  : >30bb   → shove de stack profundo, raro pero lo capturamos

export type ShoverStackBin = 'micro' | 'short' | 'mid' | 'deep';

export const SHOVER_STACK_BINS: ShoverStackBin[] = ['micro', 'short', 'mid', 'deep'];

/** Clasifica el stack del shover en BB en un bin discreto. */
export function classifyShoverStack(stackBB: number): ShoverStackBin {
  if (!Number.isFinite(stackBB) || stackBB < 0) return 'micro';
  if (stackBB <= 10) return 'micro';
  if (stackBB <= 20) return 'short';
  if (stackBB <= 30) return 'mid';
  return 'deep';
}

/** Contador raw de fold-to-push por bin o global. */
export interface FoldToPushStats {
  opp: number;   // veces que hero enfrentó el shove con cartas vivas
  folds: number; // veces que hero foldeó
}

// ─────────────────────────────────────────────────────────────────────────────
// VPIP por posición — voluntarily put money in pot desglosado por posición
// ─────────────────────────────────────────────────────────────────────────────
// Usa Position (6 posiciones + BB) porque BB SÍ tiene VPIP relevante (defenderse
// con call a un raise es VPIP). A diferencia de RFI, BB no puede abrir pero
// sí puede meter dinero voluntariamente.

export interface VpipPositionStats {
  opp: number;   // manos jugadas en esa posición
  count: number; // veces que hero metió dinero voluntariamente (call o raise preflop)
}

// ─────────────────────────────────────────────────────────────────────────────
// RFI (Raise First In) por posición — Fase de open raise
// ─────────────────────────────────────────────────────────────────────────────

export interface RfiPositionStats {
  opp: number;      // veces que hero tuvo la oportunidad de abrir (todos foldearon antes)
  count: number;    // veces que hero raiseó
  limps?: number;   // veces que hero llamó (open-limp — leak)
}

// ─────────────────────────────────────────────────────────────────────────────
// Push/Fold short stack stats — Fase A
// ─────────────────────────────────────────────────────────────────────────────
//
// Tracking de acciones de hero cuando:
//   1. Hero tiene stack <= PUSH_FOLD_MAX_STACK_BB (15bb por default)
//   2. La mesa foldeó a hero (tableFoldedToHero === true)
//   3. Hero tomó una acción (no foldeó antes de actuar)
//
// Esto captura los spots donde la teoría push/fold aplica. La acción de hero
// se clasifica en 4 categorías:
//
//   - pushes:    hero shoveó all-in (la acción correcta en este territorio)
//   - limps:     hero llamó (open-limp — leak universal a <15bb, target 0%)
//   - minRaises: hero raiseó pero NO all-in (leak — te committeás de todas formas)
//   - folds:     hero foldeó
//
// Note: `opp = pushes + limps + minRaises + folds`
export interface PushFoldStats {
  opp: number;
  pushes: number;
  limps: number;
  minRaises: number;
  folds: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// CBet Flop por textura × posición — Fase B
// ─────────────────────────────────────────────────────────────────────────────
//
// Solo cuenta HU flops (pot heads-up al flop). Multiway se excluye porque
// tiene dinámica completamente distinta y benchmarks distintos.
//
// Key format: `${texture}-${posType}` donde:
//   texture = dry | semi-wet | wet | paired | monotone (tu FlopTexture existente)
//   posType = 'ip' | 'oop'
//
// IP = hero está en late position (BTN/CO/HJ/EP) vs blind
// OOP = hero está en blind (SB/BB) vs late position raiser
export type CbetTexturePosKey =
  | 'dry-ip' | 'dry-oop'
  | 'semi-wet-ip' | 'semi-wet-oop'
  | 'wet-ip' | 'wet-oop'
  | 'paired-ip' | 'paired-oop'
  | 'monotone-ip' | 'monotone-oop';

export type IpOopType = 'ip' | 'oop';

export function makeCbetTextureKey(texture: FlopTexture, pos: IpOopType): CbetTexturePosKey {
  return `${texture}-${pos}` as CbetTexturePosKey;
}

export interface CbetTextureStats {
  opp: number;     // veces que hero era PFR y llegó al flop HU
  count: number;   // veces que hero efectivamente hizo cbet (bet antes que el villain o bet después de check del villain)
}

// ─────────────────────────────────────────────────────────────────────────────
// CBet Flop por textura — MULTIWAY (Fase B.2)
// ─────────────────────────────────────────────────────────────────────────────
//
// Complementa Fase B.1 (HU). Cuando hero es PFR y llega al flop con 2+
// villains, trackeamos cbet frequency por textura SOLA (sin IP/OOP, porque
// en multiway el concepto de "in position" es ambiguo — tenés varios villains
// detrás y otros adelante).
//
// Las texturas comparten la misma escala que FlopTexture. La key es la
// textura cruda. Los benchmarks multiway son sustancialmente más bajos que
// los HU porque el rango advantage del PFR se diluye con cada villain extra.
export type CbetTextureMultiKey = FlopTexture; // 'dry' | 'semi-wet' | 'wet' | 'paired' | 'monotone'

// Las posiciones que pueden ser "openers". SB/BB no incluyen porque desde
// blinds tu acción no es "open raise" sino "complete + raise" (otra dinámica).
// La SB sí podría considerarse open raiser cuando todos foldean al SB —
// la incluimos para cubrir ese caso (ATS desde SB).
export type OpenPosition = 'EP' | 'HJ' | 'CO' | 'BTN' | 'SB';

export const OPEN_POSITIONS: OpenPosition[] = ['EP', 'HJ', 'CO', 'BTN', 'SB'];

// ─────────────────────────────────────────────────────────────────────────────
// HistoryStats — contador principal agregado por torneo
// ─────────────────────────────────────────────────────────────────────────────

export interface HistoryStats {
  // Counters preflop
  afChecks: number;
  handsPlayed: number;
  positionCounts: Record<Position, number>;

  vpipCount: number;
  pfrCount: number;
  threebetCount: number;
  threebetOpp: number;
  foldTo3betCount: number;
  foldTo3betOpp: number;

  // Fase 2: split de Fold to 3Bet por all-in vs no all-in
  foldTo3betAllinOpp?: number;
  foldTo3betAllinCount?: number;
  foldTo3betNonAllinOpp?: number;
  foldTo3betNonAllinCount?: number;

  // Fase 4: 3Bet por matchup posicional (hero como 3bettor)
  // Key: `${heroPos}_vs_${openerPos}` — ej: "SB_vs_BTN" significa que hero
  // 3beteó desde SB sobre un open de BTN.
  threebetByMatchup?: Partial<Record<MatchupKey, MatchupStats>>;

  // Fase 4: Fold to 3Bet por matchup posicional (hero como opener enfrentando 3bet)
  // Key: `${heroPos}_vs_${threebettorPos}` — ej: "BTN_vs_SB" significa que hero
  // abrió desde BTN y enfrentó un 3bet desde SB.
  foldTo3betByMatchup?: Partial<Record<MatchupKey, MatchupStats>>;

  // Fase C: Fold to Push (hero enfrenta shove all-in de villain)
  foldToPushOpp?: number;
  foldToPushCount?: number;  // veces que hero foldeó (= folds)
  foldToPushByShoverStack?: Partial<Record<ShoverStackBin, FoldToPushStats>>;

  // VPIP por posición — usa Position (incluye BB)
  vpipByPosition?: Partial<Record<Position, VpipPositionStats>>;

  // Open raise (RFI) por posición — Fase 3
  rfiByPosition?: Partial<Record<OpenPosition, RfiPositionStats>>;

  // Push/Fold short stack (Fase A) por posición — solo cuenta cuando
  // heroStackBB <= 15 y la mesa foldeó a hero (true RFI opportunity).
  pushFoldByPosition?: Partial<Record<OpenPosition, PushFoldStats>>;

  // CBet Flop por textura × IP/OOP — Fase B (HU only)
  // Se popula cuando hero fue PFR, llegó al flop heads-up, y tuvo la
  // oportunidad de cbet (el villain no le bateó primero).
  cbetFlopByTexture?: Partial<Record<CbetTexturePosKey, CbetTextureStats>>;

  // CBet Flop por textura — MULTIWAY (Fase B.2)
  // Se popula cuando hero fue PFR, llegó al flop con 2+ villains, y tuvo
  // la oportunidad de cbet. Sin IP/OOP — solo por textura.
  cbetFlopByTextureMulti?: Partial<Record<CbetTextureMultiKey, CbetTextureStats>>;

  atsCount: number;
  atsOpp: number;

  // Aggression factor
  afBets: number;
  afRaises: number;
  afCalls: number;

  // Cbet counters — flop/turn/river
  cbetFlopCount: number;
  cbetFlopOpp: number;
  cbetFlopSuccessCount: number;
  cbetTurnCount: number;
  cbetTurnOpp: number;
  cbetTurnSuccessCount: number;
  cbetRiverCount: number;
  cbetRiverOpp: number;
  cbetRiverSuccessCount: number;

  // Fase 1: cbet flop por matchup posicional
  cbetFlopMatchups?: Partial<Record<MatchupKey, MatchupStats>>;

  // Defense
  foldToCbetFlopCount: number;
  foldToCbetFlopOpp: number;
  callCbetFlopCount: number;
  foldToCbetTurnCount: number;
  foldToCbetTurnOpp: number;
  callCbetTurnCount: number;
  foldToCbetRiverCount: number;
  foldToCbetRiverOpp: number;
  callCbetRiverCount: number;

  // Donk/probe
  donkFlopCount: number;
  donkFlopOpp: number;
  donkTurnCount: number;
  donkTurnOpp: number;
  donkRiverCount: number;
  donkRiverOpp: number;
  probeTurnCount: number;
  probeTurnOpp: number;
  probeRiverCount: number;
  probeRiverOpp: number;

  // Showdown
  flopsSeen: number;
  wtsdCount: number;
  wsdCount: number;
  wsdOpp: number;
  wwsfCount: number;
  wwsfOpp: number;

  // Extra stats (opcionales, no todos los torneos los tienen)
  bbDefendOpp?: number;
  bbDefendCall?: number;
  bbDefendRaise?: number;
  bbDefendFold?: number;

  flopDryCount?: number;
  cbetFlopDryCount?: number;
  cbetFlopDrySuccessCount?: number;

  btnStealOpp?: number;
  btnStealCount?: number;
  sbStealOpp?: number;
  sbStealCount?: number;

  doubleBarrelOpp?: number;
  doubleBarrelCount?: number;

  // Delayed CBet: Hero PFA, flop check-check, Hero primera apuesta en turn
  delayCbetOpp?: number;
  delayCbetCount?: number;

  // CBet en pozo 3beteado (Hero fue el 3bettor, es el PFA postflop)
  cbet3BetPotOpp?: number;
  cbet3BetPotCount?: number;
  cbet3BetPotIPOpp?: number;
  cbet3BetPotIPCount?: number;
  cbet3BetPotOOPOpp?: number;
  cbet3BetPotOOPCount?: number;

  // Squeeze: hero raises con 1 raiser + 1+ callers antes de actuar
  squeezeOpp?: number;
  squeezeCount?: number;

  // 4bet: hero enfrenta 2 raises antes de actuar (open + 3bet) y puede re-raise
  fourbetOpp?: number;
  fourbetCount?: number;

  // Check-raise flop: hero chequeó, villain beteó, hero raiseó
  // Segmentado por si hero era PFA (fue el preflop raiser) o no.
  xraiseFlopOpp?: number;
  xraiseFlopCount?: number;
  xraiseFlopAsPFAOpp?: number;
  xraiseFlopAsPFACount?: number;
  xraiseFlopNonPFAOpp?: number;
  xraiseFlopNonPFACount?: number;

  bountiesCollected?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// ParsedTournament — torneo completo después de parsear
// ─────────────────────────────────────────────────────────────────────────────

export interface ParsedTournament extends HistoryStats {
  filename: string;
  tournamentId: string;
  name: string;
  rawName: string;
  format: GameFormat;

  summaryLoaded: boolean;
  historyLoaded: boolean;

  buyIn: number;
  totalCost: number;          // buyIn × (1 + rebuys) — agregado para consistencia
  rake: number;
  bountyComponent: number;
  buyInUnknown: boolean;
  prizeTicket: number;

  totalPlayers: number;
  prizePool: number;
  heroFinishPosition: number | null;
  heroPrize: number;
  heroPrizeIsTicket: boolean;
  heroPrizeTicketValue: number;
  heroPrizeTournamentDollars: number;
  heroItm: boolean;
  rebuys: number;

  heroBusted: boolean;
  date: string;
  time: string;
  avgStackDepthBB: number;

  statsByStack: Record<StackBucket, HistoryStats>;
}

// ─────────────────────────────────────────────────────────────────────────────
// DerivedStats — stats calculados como porcentajes
// ─────────────────────────────────────────────────────────────────────────────

export interface MatchupDerived {
  key: MatchupKey;
  heroPos: Position | null;
  villainPos: Position | null;
  opp: number;
  count: number;
  success: number;
  cbetPct: number;      // count / opp
  successPct: number;   // success / count
  lowSample: boolean;   // opp < MIN_MATCHUP_SAMPLE
}

// Fase C: Fold to Push por stack bin — derivado para UI
export interface FoldToPushDerived {
  bin: ShoverStackBin;
  label: string;     // '≤10bb', '10-20bb', '20-30bb', '>30bb'
  opp: number;
  folds: number;
  foldPct: number;   // folds / opp
  lowSample: boolean;
}

// VPIP por posición — derivado para UI
export interface VpipDerived {
  position: Position;
  opp: number;
  count: number;
  pct: number;        // count / opp
  lowSample: boolean; // opp < MIN_VPIP_SAMPLE
}

// Fase 3: RFI por posición — derivado
export interface RfiDerived {
  position: OpenPosition;
  opp: number;
  count: number;
  limps: number;
  pct: number;          // raises / opp
  limpPct: number;      // limps / opp
  lowSample: boolean;   // opp < MIN_RFI_SAMPLE
}

// Fase A: Push/Fold por posición — derivado para UI
export interface PushFoldDerived {
  position: OpenPosition;
  opp: number;
  pushes: number;
  limps: number;
  minRaises: number;
  folds: number;
  pushPct: number;      // pushes / opp
  limpPct: number;      // limps / opp  (any value > 0 is a leak)
  minRaisePct: number;  // minRaises / opp  (any value > 0 is a leak)
  foldPct: number;      // folds / opp
  lowSample: boolean;   // opp < MIN_PUSHFOLD_SAMPLE
}

// Fase B: CBet Flop por textura × posición — derivado para UI
export interface CbetTextureDerived {
  key: CbetTexturePosKey;
  texture: FlopTexture;
  posType: IpOopType;
  opp: number;
  count: number;
  cbetPct: number;      // count / opp
  lowSample: boolean;   // opp < MIN_TEXTURE_SAMPLE
}

// Fase B.2: CBet Flop por textura — MULTIWAY (sin posición)
export interface CbetTextureMultiDerived {
  texture: FlopTexture;
  opp: number;
  count: number;
  cbetPct: number;
  lowSample: boolean;
}

export interface DerivedStats {
  vpip: number;
  pfr: number;
  pfrVpipRatio: number;
  threebet: number;
  foldTo3bet: number;
  ats: number;
  af: number;
  afq: number;

  // Fase 2: splits de fold to 3bet
  foldTo3betAllin: number;
  foldTo3betNonAllin: number;
  foldTo3betAllinOpp: number;
  foldTo3betNonAllinOpp: number;

  // Fase C: Fold to Push
  foldToPush: number;       // % global de fold cuando enfrentás un shove
  foldToPushOpp: number;
  foldToPushByShoverStack: FoldToPushDerived[];

  // VPIP por posición (6 posiciones: EP/HJ/CO/BTN/SB/BB)
  vpipByPosition: VpipDerived[];
  vpipTotalOpp: number;

  // Fase 3: RFI por posición
  rfiByPosition: RfiDerived[];
  rfiTotal: number;       // RFI agregado (count / opp sumado)
  rfiTotalOpp: number;

  // Fase A: Push/Fold short stack por posición
  pushFoldByPosition: PushFoldDerived[];
  pushFoldTotalOpp: number;

  // Fase B: CBet Flop por textura × posición (HU only)
  cbetFlopByTexture: CbetTextureDerived[];
  cbetFlopByTextureTotalOpp: number;

  // Fase B.2: CBet Flop por textura — MULTIWAY (sin IP/OOP)
  cbetFlopByTextureMulti: CbetTextureMultiDerived[];
  cbetFlopByTextureMultiTotalOpp: number;

  cbetFlop: number;
  cbetFlopSuccess: number;
  cbetTurn: number;
  cbetTurnSuccess: number;
  cbetRiver: number;
  cbetRiverSuccess: number;
  delayCbet: number;
  delayCbetOpp: number;

  // CBet en pozo 3beteado: global + split IP/OOP
  cbet3BetPot: number;
  cbet3BetPotOpp: number;
  cbet3BetPotIP: number;
  cbet3BetPotIPOpp: number;
  cbet3BetPotOOP: number;
  cbet3BetPotOOPOpp: number;

  // Fase 1: matchup breakdown del cbet flop
  cbetFlopByMatchup: MatchupDerived[];

  // Fase 4: matchup breakdowns del 3bet (hero como 3bettor)
  // y del fold to 3bet (hero como opener enfrentando 3bet)
  threebetByMatchup: MatchupDerived[];
  foldTo3betByMatchup: MatchupDerived[];

  foldToCbetFlop: number;
  callCbetFlop: number;
  foldToCbetTurn: number;
  callCbetTurn: number;
  foldToCbetRiver: number;
  callCbetRiver: number;

  donkFlop: number;
  donkTurn: number;
  donkRiver: number;
  probeTurn: number;
  probeRiver: number;
  probeTotal: number;

  wtsd: number;
  wwsf: number;
  wsd: number;
  positionBreakdown: Record<Position, number>;

  bbDefendPct?: number;
  cbetFlopDryPct?: number;
  btnStealPct?: number;
  sbStealPct?: number;
  doubleBarrelPct?: number;

  // Squeeze preflop
  squeeze?: number;
  squeezeOpp?: number;

  // 4bet preflop
  fourbet?: number;
  fourbetOpp?: number;

  // Open limp (global, suma de todas las posiciones)
  openLimp?: number;
  openLimpOpp?: number;

  // Check-raise flop
  xraiseFlopPct?: number;
  xraiseFlopOpp?: number;
  xraiseFlopAsPFAPct?: number;
  xraiseFlopAsPFAOpp?: number;
  xraiseFlopNonPFAPct?: number;
  xraiseFlopNonPFAOpp?: number;
}
